<template>
  <div class="owners-table">
    <div class="title-container">
      <h2>{{ t('workspaces.owners') }}</h2>

      <vf-button
        class="action-button"
        :size="buttonSize"
        :variant="buttonVariant"
        @click="onAddParticipantButtonClick"
        >{{ t(addActionLabelKey) }}
      </vf-button>
    </div>
  </div>

  <user-select-modal
    ref="modalRef"
    modal-id="workspace-participants-modal"
    :ok-button-busy="isAddingWorkspaceParticipant"
    :placeholder="t(selectParticipantPlaceholderKey)"
    :search-users="searchCandidates"
    :selection-required-message="t(selectionRequiredMessageKey)"
    :title="t(addActionLabelKey)"
    @selected-user="handleUserSelected"
  />

  <table-wrapper
    :fields="tableFields"
    :items="ownerCandidates"
    :mode="TableMode.CLIENT_SIDE"
    :action-icon="'delete'"
    :paginator="false"
    :hide-reset-filter-button="true"
    :empty-table-text="'workspaces.noOwnerSelected'"
    @action-icon-click="handleRemoveOwnerClick"
  />
</template>

<script lang="ts" setup>
  import { TableFieldsBuilder, TableMode } from '@/components/TableWrapper';
  import { IAdUserInternalFormat } from 'types';
  import { computed, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useWorkspaceParticipants } from '@/composables/api/workspaces/useWorkspaceParticipants';
  import UserSelectModal from '../UserSelectModal/UserSelectModal.vue';
  import { ButtonSize, ButtonVariant } from '../VfButton/VfButton.types';

  const selectedOwners = defineModel<IAdUserInternalFormat[]>(
    'selected-owners',
    {
      default: () => [],
    },
  );

  const { t } = useI18n();
  const ownerCandidates = ref<IAdUserInternalFormat[]>([]);

  const buttonVariant = ButtonVariant.SECONDARY;
  const buttonSize = ButtonSize.LARGE;

  const { searchCandidates, isAddingWorkspaceParticipant } =
    useWorkspaceParticipants('owner');

  const tableFields = computed(() =>
    new TableFieldsBuilder<IAdUserInternalFormat>({
      sortable: false,
      labelTranslationKey: 'generic',
    })
      .addField('firstName', {
        width: '36%',
      })
      .addField('lastName', {
        width: '28%',
      })
      .addField('email', {
        width: '34%',
      })
      .build(),
  );

  const modalRef = ref<InstanceType<typeof UserSelectModal> | null>(null);

  function onAddParticipantButtonClick(): void {
    modalRef.value?.show();
  }

  function handleRemoveOwnerClick(participant: IAdUserInternalFormat): void {
    const i = ownerCandidates.value.findIndex((o) => o.oid === participant.oid);
    ownerCandidates.value.splice(i, 1);
  }

  async function handleUserSelected(
    user: IAdUserInternalFormat,
  ): Promise<void> {
    ownerCandidates.value.push(user);
  }

  const selectionRequiredMessageKey = computed(
    () => `workspaceOwnersManagement.errors.ownerSelectionRequired`,
  );

  const selectParticipantPlaceholderKey = computed(
    () => `workspaceOwnersManagement.selectOwner`,
  );

  const addActionLabelKey = computed(
    () => `workspaceOwnersManagement.addOwner`,
  );

  watch(
    () => ownerCandidates.value,
    (newVal) => {
      selectedOwners.value = newVal;
    },
    { immediate: true },
  );
</script>

<style scoped lang="scss">
  .owners-table {
    border-top: 1px solid $border-color;
    padding-top: $tab-view-margin-top;

    .title-container {
      display: flex;
      align-items: center;
      column-gap: 10px;
      margin-bottom: 2rem;
      justify-content: space-between;

      h2 {
        @include subheading;
      }

      .owners-counter {
        align-self: flex-start;
        @include heading-counter;
      }
    }
  }
</style>
